import { type FormEvent, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { Button } from '@/components/ui/Button';
import { PasswordInput } from '@/components/ui/PasswordInput';
import { useResetPassword } from '@/lib/auth';

import { AuthCard, FormField } from './LoginPage';

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const navigate = useNavigate();
  const reset = useResetPassword();

  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [done, setDone] = useState(false);

  if (!token) {
    return (
      <AuthCard
        title="Missing reset token"
        subtitle="Open the link from your email — it should include a token."
      >
        <p className="text-center text-sm text-stone-600 dark:text-stone-400">
          <Link to="/login" className="text-forge-700 dark:text-forge-400 hover:underline">
            Back to sign-in
          </Link>
        </p>
      </AuthCard>
    );
  }

  if (done) {
    return (
      <AuthCard
        title="Password reset"
        subtitle="You can sign in with your new password now."
      >
        <Button
          type="button"
          className="w-full"
          onClick={() => navigate('/login')}
        >
          Go to sign-in
        </Button>
      </AuthCard>
    );
  }

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (password !== confirmation) return;
    try {
      await reset.mutateAsync({ token, password });
      setDone(true);
    } catch {
      /* surfaced below */
    }
  };

  const mismatch = confirmation !== '' && password !== confirmation;

  return (
    <AuthCard title="Choose a new password" subtitle="Then you'll be able to sign in.">
      <form onSubmit={onSubmit} className="space-y-4">
        <FormField label="New password">
          <PasswordInput
            autoComplete="new-password"
            minLength={8}
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </FormField>
        <FormField label="Confirm new password">
          <PasswordInput
            autoComplete="new-password"
            minLength={8}
            required
            value={confirmation}
            onChange={(e) => setConfirmation(e.target.value)}
          />
        </FormField>
        {mismatch && (
          <p className="text-sm text-red-600">Passwords don't match.</p>
        )}
        {reset.isError && (
          <p className="text-sm text-red-600">
            {(reset.error as Error).message} — the token may be expired.
          </p>
        )}
        <Button
          type="submit"
          className="w-full"
          disabled={reset.isPending || mismatch || !password}
        >
          {reset.isPending ? 'Updating…' : 'Update password'}
        </Button>
      </form>
    </AuthCard>
  );
}
